import React from 'react';
import * as IoC from 'typescript-ioc';
import classnames from 'classnames';
import { IRoomDetailsDTO } from '../../../../../api/rooms.dao';
import './room-list-item.scss';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import {
    getFormattedTimezoneOffset,
    utcToZonedTime
} from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import { isSameDay } from 'date-fns';
import OperatorColumn from '../../../../global/operator-column/operator-column';
import DefaultButton from '../../../../global/default-button/default-button';
import { AttendeeColumn } from '../../../../global/attendee-column/attendee-column';
import Text from '../../../../global/text/text';
import { getCompleteRoomName } from '../../../../../infrastructure/utils/common.utils';
import { OpenFrom } from '../../../../../tracking-service/tracking-values';
import { editText } from '../../../../../constants/common-labels.constants';

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

interface IProps extends IRoomDetailsDTO {
    onEdit: (roomId: number, path: OpenFrom) => void;
    eventTimezone: string;
    isFirstItemInDayGroup: boolean;
    isStartEndTimeRenderedInPreviousRow: boolean;
    isSelected: boolean;
    searchQuery?: string;
    isRoomEditable: boolean;
}

const RoomListItem = (props: IProps) => {
    const {
        eventTimezone,
        onEdit,
        name,
        label,
        roomId,
        presenters,
        operators,
        startDateTime,
        endDateTime,
        isStartEndTimeRenderedInPreviousRow,
        isFirstItemInDayGroup,
        isSelected,
        searchQuery,
        isRoomEditable
    } = props;

    const startTime = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        dateTimeFormatter.getLocalTimeFormat()
    );

    const endTime = dateTimeFormatter.dateToFormattedString(
        endDateTime,
        dateTimeFormatter.getLocalTimeFormat()
    );

    const startTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        dateTimeFormatter.getLocalTimeFormat(),
        eventTimezone
    );

    const endTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(
        endDateTime,
        dateTimeFormatter.getLocalTimeFormat(),
        eventTimezone
    );

    const isEventTimezoneTimeDifferents = startTimeInEventTimezone !== startTime;

    const timeZoneOffset = getFormattedTimezoneOffset(startDateTime);

    const handleEditClick = () => onEdit(roomId, OpenFrom.Rooms);

    const mergeWithPreviousRowClass = classnames({'merge-with-previous-row': isFirstItemInDayGroup});

    const localDayString = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
    );

    const cellEffectClasses = classnames(mergeWithPreviousRowClass, {
        'active': isSelected,
        'hoverable': !isSelected,
    });

    return (
        <tr className='room-list-item' onDoubleClick={isRoomEditable ? handleEditClick : undefined}>
            {isStartEndTimeRenderedInPreviousRow ? <td className='merge-with-previous-row'/> : (
                <td className={classnames(mergeWithPreviousRowClass)}>
                    <div className='room-list-item__header--date-and-time'>
                        {startTimeInEventTimezone} - {endTimeInEventTimezone}
                    </div>
                    { !isSameDay(startDateTime, utcToZonedTime(startDateTime, eventTimezone)) &&
                        <div className='room-list-item__text room-list-item__text--small room-list-item__text--date-and-time'
                            data-testid='local-day'>
                            {localDayString}
                        </div>
                    }
                    {isEventTimezoneTimeDifferents &&
                    <div className='room-list-item__text--small'>
                        {startTime} - {endTime} ({timeZoneOffset})
                    </div>
                    }
                </td>
            )}
            <td className={cellEffectClasses}>
                <Text tag='div' message={getCompleteRoomName({ name: name, label: label })} withEllipsis />
            </td>
            <td className={cellEffectClasses}>
                <AttendeeColumn
                    organizations={presenters}
                    searchValue={searchQuery}
                />
            </td>
            <td className={cellEffectClasses}>
                <OperatorColumn
                    operators={operators}
                    searchValue={searchQuery}
                />
            </td>
            <td className={classnames(cellEffectClasses, 'align-right')}>
                {isRoomEditable && <DefaultButton
                    onClick={handleEditClick}
                    className='visible-while-hovered'
                    modifier='slim'
                    text={editText()}
                />}
            </td>
        </tr>
    );
};

export { RoomListItem };

